import { postDataAPI, getDataAPI } from '../utils/fetchData';
import chartingService from './chartingService';

/**
 * Auto Population Service
 * Automatically populates user's apps (Catalog, Charting, Social Listening) 
 * using their artist ID captured during onboarding
 */
class AutoPopulationService {
  
  /**
   * Extract Spotify artist ID from Spotify URL
   * @param {string} spotifyUrl - Spotify artist URL
   * @returns {string|null} Spotify artist ID
   */
  extractSpotifyArtistId(spotifyUrl) {
    if (!spotifyUrl) return null;
    
    try {
      // Match patterns like:
      // https://open.spotify.com/artist/4q3ewBCX7sLwd24UFkeCmQ
      // https://open.spotify.com/artist/4q3ewBCX7sLwd24UFkeCmQ?si=...
      const idMatch = spotifyUrl.match(/artist\/([a-zA-Z0-9]+)/);
      return idMatch ? idMatch[1] : null;
    } catch (error) {
      console.error('❌ Error extracting Spotify artist ID:', error);
      return null;
    }
  }

  /**
   * Helper function to get most prevalent artist from catalog
   * @returns {Object|null} Most prevalent artist info or null
   */
  async getMostPrevalentArtistFromCatalog() {
    try {
      const token = localStorage.getItem('dizeeToken');
      if (!token) return null;
      
      console.log('📚 AutoPopulation: Getting catalog to determine most prevalent artist...');
      const catalogResponse = await getDataAPI('user/getCatalog', JSON.parse(token));
      
      if (catalogResponse?.data?.status !== 200 || !catalogResponse?.data?.data) {
        console.warn('❌ AutoPopulation: No catalog data available');
        return null;
      }

      const catalog = Array.isArray(catalogResponse.data.data) ? catalogResponse.data.data : catalogResponse.data.data.data;
      console.log('📚 AutoPopulation: Found', catalog?.length || 0, 'tracks in catalog');

      if (!catalog || catalog.length === 0) {
        console.warn('❌ AutoPopulation: Catalog is empty');
        return null;
      }

      // Count artist frequency in catalog
      const artistFrequency = {};
      catalog.forEach(track => {
        const artistName = track.artist_name || track.artist;
        if (artistName) {
          artistFrequency[artistName] = (artistFrequency[artistName] || 0) + 1;
        }
      });

      if (Object.keys(artistFrequency).length === 0) {
        console.warn('❌ AutoPopulation: No valid artists found in catalog');
        return null;
      }

      // Get most prevalent artist
      const sortedArtists = Object.entries(artistFrequency)
        .sort(([, freqA], [, freqB]) => freqB - freqA);
      
      const mostPrevalentArtist = sortedArtists[0][0];
      const frequency = sortedArtists[0][1];
      
      console.log(`🎯 AutoPopulation: Most prevalent artist in catalog: "${mostPrevalentArtist}" (${frequency} tracks)`);

      // Try to get Spotify ID for this artist - check if user has it stored
      const userArtistId = this.getUserArtistId();
      if (userArtistId) {
        console.log(`✅ AutoPopulation: Found user's Spotify artist ID: ${userArtistId}`);
        return {
          name: mostPrevalentArtist,
          spotifyId: userArtistId,
          frequency: frequency
        };
      }

      console.warn('❌ AutoPopulation: Could not find Spotify ID for most prevalent artist');
      return {
        name: mostPrevalentArtist,
        spotifyId: null,
        frequency: frequency
      };

    } catch (error) {
      console.error('❌ AutoPopulation: Error getting most prevalent artist from catalog:', error);
      return null;
    }
  }

  /**
   * Check if user needs auto-population
   * @param {Object} user - User object with social links
   * @returns {boolean} Whether auto-population is needed
   */
  async shouldAutoPopulate(user) {
    try {
      console.log('🔍 AutoPopulation: Checking if user needs auto-population...');
      console.log('🔍 AutoPopulation: User data:', { 
        name: user?.name || user?.username, 
        spotifyUrl: user?.social?.sf,
        hasToken: !!localStorage.getItem('dizeeToken')
      });

      const token = JSON.parse(localStorage.getItem('dizeeToken'));
      if (!token) {
        console.log('❌ AutoPopulation: No authentication token found');
        return false;
      }

      // Check for Spotify link in multiple locations
      const spotifyUrl = user?.social?.sf || 
                        user?.spotify || 
                        user?.spotifyUrl ||
                        // Also check localStorage user data as fallback
                        (() => {
                          try {
                            const storedUser = JSON.parse(localStorage.getItem('dizeeUser') || '{}');
                            return storedUser?.social?.sf || storedUser?.spotify || storedUser?.spotifyUrl;
                          } catch (e) {
                            return null;
                          }
                        })();

      if (!spotifyUrl) {
        console.log('🔍 AutoPopulation: No Spotify URL found for auto-population');
        console.log('🔍 AutoPopulation: Checked locations:', {
          'user.social.sf': user?.social?.sf,
          'user.spotify': user?.spotify,
          'user.spotifyUrl': user?.spotifyUrl,
          'localStorage.user.social.sf': (() => {
            try {
              const storedUser = JSON.parse(localStorage.getItem('dizeeUser') || '{}');
              return storedUser?.social?.sf;
            } catch (e) {
              return 'error reading';
            }
          })()
        });
        return false;
      }

      console.log('🔍 AutoPopulation: Spotify URL found:', spotifyUrl);

      // Check if user already has data in any of the apps
      console.log('🔍 AutoPopulation: Checking existing data in apps...');
      const [catalogResponse, chartResponse] = await Promise.allSettled([
        getDataAPI('user/getCatalog', token),
        getDataAPI('user/songstats/catalog/artist_activities?activity_type=chart&limit=5', token)
      ]);

      const hasCatalogData = catalogResponse.status === 'fulfilled' && 
                            catalogResponse.value?.data?.status === 200 && 
                            Array.isArray(catalogResponse.value?.data?.data) &&
                            catalogResponse.value?.data?.data?.length > 0;

      const hasChartData = chartResponse.status === 'fulfilled' && 
                          chartResponse.value?.data?.status === 200 && 
                          chartResponse.value?.data?.data?.all_activities?.length > 0;

      console.log('🔍 AutoPopulation: Existing data check:', {
        hasCatalogData,
        hasChartData,
        catalogCount: catalogResponse.value?.data?.data?.length || 0,
        chartCount: chartResponse.value?.data?.data?.all_activities?.length || 0,
        catalogStatus: catalogResponse.status,
        chartStatus: chartResponse.status,
        catalogResponse: catalogResponse.status === 'fulfilled' ? catalogResponse.value?.data : catalogResponse.reason
      });

      if (hasCatalogData || hasChartData) {
        console.log('🔍 AutoPopulation: User already has data, skipping auto-population');
        return false;
      }

      console.log('✅ AutoPopulation: User needs auto-population');
      return true;

    } catch (error) {
      console.error('❌ AutoPopulation: Error checking auto-population need:', error);
      return false;
    }
  }

  /**
   * Populate user's catalog with their artist data
   * @param {string} spotifyUrl - Spotify artist URL
   * @param {string} artistName - Artist name (optional)
   * @returns {Promise<boolean>} Success status
   */
  async populateCatalog(spotifyUrl, artistName = null) {
    try {
      console.log('📚 Auto-populating catalog...');
      const token = JSON.parse(localStorage.getItem('dizeeToken'));
      
      const requestData = {
        spotifyUrl: spotifyUrl,
        artistName: artistName
      };

      // Add autoSave=true for auto-population mode (bypasses review)
      const response = await postDataAPI("user/discoverCatalogFromArtist?autoSave=true", requestData, token, {
        timeout: 120000 // 2 minutes timeout
      });

      console.log('🔍 DEBUGGING AUTO-POPULATION RESPONSE:');
      console.log('Full response:', JSON.stringify(response, null, 2));
      console.log('response.data:', response.data);
      console.log('response.data?.success:', response.data?.success);
      console.log('response.data?.data:', response.data?.data);
      console.log('response.data?.data?.success:', response.data?.data?.success);

      // Check for success at the correct nested path
      const isSuccess = response.data?.data?.success || response.data?.success;
      
      if (isSuccess) {
        const catalogData = response.data?.data?.data || response.data?.data || [];
        console.log(`✅ Auto-populated catalog with ${catalogData.length} tracks`);
        return true;
      } else {
        console.warn('⚠️ Catalog auto-population failed:', response.data?.message);
        console.log('❌ Failed because success was not found at either path');
        console.log('response.data?.success:', response.data?.success);
        console.log('response.data?.data?.success:', response.data?.data?.success);
        return false;
      }

    } catch (error) {
      console.error('❌ Error auto-populating catalog:', error);
      return false;
    }
  }

  /**
   * Populate user's charting data using direct artist ID
   * @param {string} spotifyArtistId - Spotify artist ID
   * @returns {Promise<boolean>} Success status
   */
  async populateChartingData(spotifyArtistId) {
    try {
      console.log('📊 Auto-populating charting data for most prevalent catalog artist...');

      // Step 1: Get most prevalent artist from catalog
      const prevalentArtist = await this.getMostPrevalentArtistFromCatalog();
      
      if (!prevalentArtist || !prevalentArtist.spotifyId) {
        console.error('❌ AutoPopulation: Could not determine most prevalent artist or get Spotify ID');
        return false;
      }

      console.log(`🎯 AutoPopulation: Loading chart data for: ${prevalentArtist.name} (${prevalentArtist.spotifyId})`);

      // Step 2: Get chart activities for this specific artist only
      const artistActivitiesData = await chartingService.getArtistActivities(prevalentArtist.spotifyId, {
        activity_type: 'chart',
        time_period: '30d',
        limit: 100
      });
      
      console.log('📊 AutoPopulation: Raw artist activities response:', artistActivitiesData);

      if (artistActivitiesData && artistActivitiesData.activities && artistActivitiesData.activities.length > 0) {
        console.log('📊 AutoPopulation: Chart activities found:', artistActivitiesData.activities.length);
        
        const processedChartData = chartingService.processChartActivities({
          all_activities: artistActivitiesData.activities
        });
        console.log('📊 AutoPopulation: Processed chart data:', processedChartData.length, 'entries');
        
        // Set single artist data (matching ChartingApp format)
        const artistsData = [{
          name: prevalentArtist.name,
          loading: false,
          frequency: prevalentArtist.frequency,
          spotifyId: prevalentArtist.spotifyId
        }];

        // SAVE TO LOCALSTORAGE - Same format as ChartingApp expects
        const CACHE_KEY = 'charting_app_data';
        const dataToCache = {
          chartData: processedChartData,
          summaryData: {
            total_artists: 1,
            total_activities: processedChartData.length
          },
          catalogArtists: artistsData,
          timestamp: Date.now()
        };
        
        try {
          localStorage.setItem(CACHE_KEY, JSON.stringify(dataToCache));
          console.log(`✅ Auto-populated charting data with ${processedChartData.length} activities for most prevalent artist`);
          return true;
        } catch (cacheError) {
          console.error('❌ Error saving charting data to localStorage:', cacheError);
          return false;
        }
        
      } else {
        console.log('📊 AutoPopulation: No chart activities found for most prevalent artist');
        
        // Still save the artist info even with no data
        const artistsData = [{
          name: prevalentArtist.name,
          loading: false,
          frequency: prevalentArtist.frequency,
          spotifyId: prevalentArtist.spotifyId
        }];

        const CACHE_KEY = 'charting_app_data';
        const dataToCache = {
          chartData: [],
          summaryData: {
            total_artists: 1,
            total_activities: 0
          },
          catalogArtists: artistsData,
          timestamp: Date.now()
        };
        
        try {
          localStorage.setItem(CACHE_KEY, JSON.stringify(dataToCache));
          console.log('✅ Auto-populated charting structure (no activities found)');
          return true;
        } catch (cacheError) {
          console.error('❌ Error saving empty charting data to localStorage:', cacheError);
          return false;
        }
      }

    } catch (error) {
      console.error('❌ Error auto-populating charting data:', error);
      return false;
    }
  }

  /**
   * Populate user's social listening data using direct artist ID
   * @param {string} spotifyArtistId - Spotify artist ID
   * @returns {Promise<boolean>} Success status
   */
  async populateSocialData(spotifyArtistId) {
    try {
      console.log('💬 Auto-populating social data for most prevalent catalog artist...');

      // Step 1: Get most prevalent artist from catalog
      const prevalentArtist = await this.getMostPrevalentArtistFromCatalog();
      
      if (!prevalentArtist || !prevalentArtist.spotifyId) {
        console.error('❌ AutoPopulation: Could not determine most prevalent artist or get Spotify ID');
        return false;
      }

      console.log(`🎯 AutoPopulation: Loading social data for: ${prevalentArtist.name} (${prevalentArtist.spotifyId})`);

      // Step 2: Get social activities for this specific artist only
      const artistActivitiesData = await chartingService.getArtistActivities(prevalentArtist.spotifyId, {
        activity_type: 'comment', // Filter for social/comment activities
        time_period: '30d',
        limit: 100
      });
      
      console.log('📊 AutoPopulation: Raw artist social activities response:', artistActivitiesData);

      if (artistActivitiesData && artistActivitiesData.activities && artistActivitiesData.activities.length > 0) {
        console.log('📊 AutoPopulation: Social activities found:', artistActivitiesData.activities.length);
        
        // Use the SAME processing logic as SocialListeningApp
        const processedSocialData = this.processSocialActivities({
          all_activities: artistActivitiesData.activities
        });
        console.log('📊 AutoPopulation: Processed social data:', processedSocialData.length, 'entries');

        // Set single artist data (matching SocialListeningApp format)
        const artistsData = [{
          name: prevalentArtist.name,
          loading: false,
          frequency: prevalentArtist.frequency,
          spotifyId: prevalentArtist.spotifyId
        }];

        // SAVE TO LOCALSTORAGE - Same format as SocialListeningApp expects
        const CACHE_KEY = 'social_listening_app_data';
        const dataToCache = {
          socialData: processedSocialData,
          summaryData: {
            total_artists: 1,
            total_activities: processedSocialData.length
          },
          catalogArtists: artistsData,
          timestamp: Date.now()
        };
        
        try {
          localStorage.setItem(CACHE_KEY, JSON.stringify(dataToCache));
          console.log(`✅ Auto-populated social data with ${processedSocialData.length} activities for most prevalent artist`);
          return true;
        } catch (cacheError) {
          console.error('❌ Error saving social data to localStorage:', cacheError);
          return false;
        }
        
      } else {
        console.log('📊 AutoPopulation: No social activities found for most prevalent artist');
        
        // Still save the artist info even with no data
        const artistsData = [{
          name: prevalentArtist.name,
          loading: false,
          frequency: prevalentArtist.frequency,
          spotifyId: prevalentArtist.spotifyId
        }];

        const CACHE_KEY = 'social_listening_app_data';
        const dataToCache = {
          socialData: [],
          summaryData: {
            total_artists: 1,
            total_activities: 0
          },
          catalogArtists: artistsData,
          timestamp: Date.now()
        };
        
        try {
          localStorage.setItem(CACHE_KEY, JSON.stringify(dataToCache));
          console.log('✅ Auto-populated social structure (no activities found)');
          return true;
        } catch (cacheError) {
          console.error('❌ Error saving empty social data to localStorage:', cacheError);
          return false;
        }
      }

    } catch (error) {
      console.error('❌ Error auto-populating social data:', error);
      return false;
    }
  }

  /**
   * Process social activities data into standardized format
   * This is the SAME logic as SocialListeningApp.processSocialActivities
   * @param {Object} activitiesData - Raw activities data from API
   * @returns {Array} Processed social data
   */
  processSocialActivities(activitiesData) {
    if (!activitiesData || !activitiesData.all_activities) {
      return [];
    }

    const { all_activities } = activitiesData;
    
    console.log('🔍 Processing social activities:', all_activities.length);
    console.log('🔍 First activity sample:', all_activities[0]);
    console.log('🔍 Activity types found:', [...new Set(all_activities.map(a => a.activity_type))]);
    
    return all_activities
      .filter(activity => {
        const activityType = activity.activity_type || activity.type || '';
        const activityText = activity.activity_text || '';
        
        // Filter for social/comment activities
        const isSocial = activityType === 'comment' ||
                        activityType === 'mention' ||
                        activityType === 'social_post' ||
                        activityType === 'review' ||
                        activityType === 'user_generated_content' ||
                        activityType.toLowerCase().includes('social') ||
                        activityType.toLowerCase().includes('comment') ||
                        activityType.toLowerCase().includes('mention') ||
                        activityText.toLowerCase().includes('comment') ||
                        activityText.toLowerCase().includes('mention') ||
                        activityText.toLowerCase().includes('post') ||
                        activityText.toLowerCase().includes('review');
        
        // Log what we're including/excluding
        if (isSocial) {
          console.log('✅ Including social activity:', activityType, '-', activityText.substring(0, 80) + '...');
        } else {
          console.log('🚫 Excluding non-social activity:', activityType, '-', activityText.substring(0, 50) + '...');
        }
        
        return isSocial;
      })
      .map(activity => {
        const artistInfo = activity.artist_info || {};
        const trackInfo = activity.track_info || {};
        
        const content = activity.activity_text || activity.comment_text || activity.description || 'Social mention';
        
        return {
          id: activity.id || activity.activity_id,
          platform: this.determinePlatform(activity.source || activity.platform),
          curator_name: this.extractUserName(activity, content),
          username: activity.username || activity.user_handle || activity.curator_handle,
          content: content,
          created_at: activity.date || activity.activity_date || activity.created_at,
          likes: activity.likes || activity.reactions || 0,
          shares: activity.shares || activity.reposts || 0,
          comments: activity.comments || activity.replies || 0,
          reach: activity.reach || activity.impressions || '0',
          views: activity.views || activity.video_views || 0,
          creates: activity.creates || activity.video_creates || 0,
          sentiment: activity.sentiment,
          track_name: activity.track_name || activity.song_name || trackInfo.title,
          artist_name: activity.artist_name || activity.artist || artistInfo.artist_name || trackInfo.artists?.[0]?.name,
          data_source: 'songstats_direct_artist',
          activity_type: activity.activity_type,
          activity_text: activity.activity_text,
          activity_url: activity.activity_url,
          activity_avatar: activity.activity_avatar,
          activity_tier: activity.activity_tier
        };
      })
      .filter(item => {
        // Only require basic social information
        return item.content && item.artist_name;
      })
      .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)); // Sort by newest first
  }

  /**
   * Extract user name from activity data or content
   * Same logic as SocialListeningApp.extractUserName
   */
  extractUserName(activity, content) {
    // Try direct fields first
    const directName = activity.curator_name || activity.user_name || activity.author_name;
    if (directName && directName !== 'Unknown User') {
      return directName;
    }
    
    // Extract from content text patterns
    if (content) {
      // Pattern: "New video by [Name] ([Followers])" - for TikTok/Instagram videos
      const videoWithFollowersMatch = content.match(/New video by\s+([^(]+?)\s*\(/i);
      if (videoWithFollowersMatch && videoWithFollowersMatch[1]) {
        return videoWithFollowersMatch[1].trim();
      }
      
      // Pattern: "New video by [Name]" without parentheses
      const videoMatch = content.match(/New video by\s+(.+?)(?:\s*$)/i);
      if (videoMatch && videoMatch[1]) {
        return videoMatch[1].trim();
      }
      
      // Pattern: "New Comment by [Name] ([Subscribers])" - most specific first
      const commentWithSubsMatch = content.match(/New Comment by\s+([^(]+?)\s*\(/i);
      if (commentWithSubsMatch && commentWithSubsMatch[1]) {
        return commentWithSubsMatch[1].trim();
      }
      
      // Pattern: "New Comment by [Name]" without parentheses
      const commentMatch = content.match(/New Comment by\s+(.+?)(?:\s*$)/i);
      if (commentMatch && commentMatch[1]) {
        return commentMatch[1].trim();
      }
      
      // Pattern: "New post by [Name]" or other content types
      const newContentMatch = content.match(/New (?:post|story|reel|short) by\s+([^(]+?)(?:\s*\(|$)/i);
      if (newContentMatch && newContentMatch[1]) {
        return newContentMatch[1].trim();
      }
      
      // Pattern: "Comment by [Name]" or "Post by [Name]" or "Video by [Name]"
      const byMatch = content.match(/(?:Comment by|Post by|Video by|by)\s+([^(]+?)(?:\s*\(|$)/i);
      if (byMatch && byMatch[1]) {
        return byMatch[1].trim();
      }
      
      // Pattern: "@username" or "username:"
      const userMatch = content.match(/^(@?\w+)[:]/);
      if (userMatch && userMatch[1]) {
        return userMatch[1].replace('@', '');
      }
      
      // Pattern: Look for names before subscriber/follower counts
      const subscriberMatch = content.match(/by\s+([^(]+?)\s*\([\d.,]+\s*(?:Subscribers?|Followers?)\)/i);
      if (subscriberMatch && subscriberMatch[1]) {
        return subscriberMatch[1].trim();
      }
    }
    
    // Fallback to username or platform default
    return activity.username || activity.user_handle || activity.curator_handle || 'Social User';
  }

  /**
   * Determine platform from source string
   * Same logic as SocialListeningApp.determinePlatform
   */
  determinePlatform(source) {
    if (!source) return 'social';
    
    const sourceStr = source.toLowerCase();
    if (sourceStr.includes('twitter') || sourceStr.includes('x.com')) return 'twitter';
    if (sourceStr.includes('instagram')) return 'instagram';
    if (sourceStr.includes('tiktok')) return 'tiktok';
    if (sourceStr.includes('youtube')) return 'youtube';
    if (sourceStr.includes('reddit')) return 'reddit';
    if (sourceStr.includes('facebook')) return 'facebook';
    if (sourceStr.includes('soundcloud')) return 'soundcloud';
    return 'social';
  }

  /**
   * Get the user's artist name from their stored data
   * @param {Array} activities - Optional: activities from API response to extract artist name
   * @param {Array} processedData - Optional: processed chart/social data to extract artist name
   * @returns {string} Artist name
   */
  getUserArtistName(activities = null, processedData = null) {
    try {
      // First try: If we have processed data (from chartingService.processChartActivities)
      if (processedData && processedData.length > 0) {
        const processedArtistName = processedData[0].artist_name;
        if (processedArtistName && 
            processedArtistName !== 'Unknown Artist' && 
            processedArtistName !== 'Unknown' &&
            processedArtistName.trim().length > 0) {
          console.log('🎯 Found artist name from processed data:', processedArtistName);
          return processedArtistName;
        }
      }

      // Second try: If we have raw activities from API response
      if (activities && activities.length > 0) {
        // Try multiple possible locations for artist name in raw API response
        for (const activity of activities.slice(0, 3)) { // Check first 3 activities for consistency
          const apiArtistName = activity.artist_name || 
                               activity.artist || 
                               activity.artist_info?.artist_name ||
                               activity.track_info?.artists?.[0]?.name ||
                               activity.track_info?.artist_name;
          
          if (apiArtistName && 
              apiArtistName !== 'Unknown Artist' && 
              apiArtistName !== 'Unknown' &&
              apiArtistName.trim().length > 0) {
            console.log('🎯 Found artist name from API response:', apiArtistName);
            return apiArtistName;
          }
        }
        
        console.log('⚠️ No valid artist name found in API activities');
        console.log('🔍 Activity sample for debugging:', {
          firstActivity: activities[0] ? {
            artist_name: activities[0].artist_name,
            artist: activities[0].artist,
            artist_info: activities[0].artist_info,
            track_info: activities[0].track_info
          } : 'No activities'
        });
      }

      // Don't fallback to username - just use a generic name
      console.log('🔍 No artist name found from API data, using default');
      return 'Your Artist';

    } catch (error) {
      console.error('❌ Error getting user artist name:', error);
      return 'Your Artist';
    }
  }

  /**
   * Get the user's artist ID from their stored data
   * This can be used by apps to load data for the user's artist
   * @returns {string|null} Spotify artist ID
   */
  getUserArtistId() {
    try {
      console.log('🎯 Getting user artist ID from stored data...');
      
      // Try multiple sources for user data
      const sources = [
        () => JSON.parse(localStorage.getItem('dizeeUser') || '{}'),
        () => JSON.parse(localStorage.getItem('dizeeUserArticle') || '{}')
      ];
      
      for (const getSource of sources) {
        try {
          const userData = getSource();
          console.log('🔍 Checking user data source:', userData);
          
          // Check for Spotify URL in all possible locations
          const spotifyUrls = [
            userData?.social?.sf,
            userData?.spotify,
            userData?.spotifyUrl,
            userData?.social?.spotify,
            userData?.social?.spotifyUrl
          ].filter(Boolean);
          
          console.log('🔍 Found Spotify URLs:', spotifyUrls);
          
          for (const spotifyUrl of spotifyUrls) {
            if (spotifyUrl && typeof spotifyUrl === 'string' && spotifyUrl.includes('spotify.com/artist/')) {
              // Extract artist ID from Spotify URL
              const artistId = this.extractSpotifyArtistId(spotifyUrl);
              
              if (artistId && artistId.length > 0) {
                console.log('✅ Found user artist ID:', artistId, 'from URL:', spotifyUrl);
                return artistId;
              }
            }
          }
        } catch (sourceError) {
          console.log('⚠️ Error reading from data source:', sourceError.message);
        }
      }

      console.log('❌ No valid Spotify artist ID found in user data');
      console.log('🔍 Available localStorage keys:', Object.keys(localStorage).filter(key => key.includes('User')));
      
      return null;

    } catch (error) {
      console.error('❌ Error getting user artist ID:', error);
      return null;
    }
  }

  /**
   * Auto-populate all user apps with their artist data
   * @param {Object} user - User object with social links
   * @param {string} artistName - Artist name from discover links (optional)
   * @returns {Promise<Object>} Population results
   */
  async autoPopulateAllApps(user, artistName = null) {
    console.log('🚀 AutoPopulation: Starting auto-population of all apps...');
    console.log('🚀 AutoPopulation: Input data:', {
      artistName,
      userHasSpotifyUrl: !!(user?.social?.sf || user?.spotify || user?.spotifyUrl),
      spotifyUrl: user?.social?.sf || user?.spotify || user?.spotifyUrl
    });
    
    try {
      // Extract Spotify URL and artist ID
      const spotifyUrl = user?.social?.sf || user?.spotify || user?.spotifyUrl;
      const spotifyArtistId = this.extractSpotifyArtistId(spotifyUrl);

      if (!spotifyUrl) {
        console.log('❌ AutoPopulation: No Spotify URL found for auto-population');
        return { success: false, message: 'No Spotify URL found' };
      }

      if (!spotifyArtistId) {
        console.log('❌ AutoPopulation: Could not extract Spotify artist ID from URL:', spotifyUrl);
        return { success: false, message: 'Invalid Spotify URL' };
      }

      console.log(`🎵 AutoPopulation: Auto-populating for artist: ${artistName || 'Unknown'} (${spotifyArtistId})`);
      console.log(`🎯 AutoPopulation: Artist ID tracked for user: ${spotifyArtistId}`);

      const results = {
        catalog: false,
        charting: false,
        social: false,
        artistId: spotifyArtistId,
        artistName: artistName
      };

      // Step 1: Populate catalog
      console.log('📚 AutoPopulation: Step 1 - Populating catalog...');
      results.catalog = await this.populateCatalog(spotifyUrl, artistName);

      // Step 2: Populate charting data (DEPENDS ON CATALOG from step 1) 
      // SAVES DATA TO LOCALSTORAGE for ChartingApp to read
      console.log('📊 AutoPopulation: Step 2 - Populating charting data using catalog approach...');
      results.charting = await this.populateChartingData(spotifyArtistId);

      // Step 3: Populate social listening data (DEPENDS ON CATALOG from step 1)
      // SAVES DATA TO LOCALSTORAGE for SocialListeningApp to read
      console.log('💬 AutoPopulation: Step 3 - Populating social data using catalog approach...');
      results.social = await this.populateSocialData(spotifyArtistId);

      const successCount = Object.values(results).filter(r => r === true).length;
      const totalSteps = 3;

      console.log(`✅ AutoPopulation: Auto-population completed: ${successCount}/${totalSteps} successful`);
      console.log('🎯 AutoPopulation: Final results:', results);

      return {
        success: results.catalog, // Primary success is catalog population
        results: results,
        message: `Auto-populated ${successCount}/${totalSteps} apps successfully`,
        spotifyArtistId: spotifyArtistId,
        artistName: artistName
      };

    } catch (error) {
      console.error('❌ AutoPopulation: Auto-population failed:', error);
      return { 
        success: false, 
        message: `Auto-population failed: ${error.message}`,
        error: error
      };
    }
  }

  // Note: Auto-population now only runs during signup, not on login

  /**
   * TESTING UTILITIES
   */

  /**
   * Clear auto-population data for testing
   * Call this in browser console: window.clearAutoPopulationData()
   */
  clearAutoPopulationData() {
    localStorage.removeItem('social_listening_app_data');
    localStorage.removeItem('charting_app_data');
    localStorage.removeItem('hasCheckedAutoPopulation');
    console.log('✅ Auto-population data cleared. Refresh the page to trigger auto-population again.');
  }

  /**
   * Reset auto-population check for testing
   * Call this in browser console: window.resetAutoPopulation()
   */
  resetAutoPopulationCheck() {
    localStorage.removeItem('hasCheckedAutoPopulation');
    console.log('✅ Auto-population check reset. Refresh the page to trigger auto-population again.');
  }

  /**
   * Force auto-population for testing
   * Call this in browser console: window.forceAutoPopulation()
   */
  async forceAutoPopulation() {
    try {
      const user = JSON.parse(localStorage.getItem('dizeeUser'));
      if (!user) {
        console.log('❌ No user found in localStorage');
        return;
      }

      console.log('🔄 Forcing auto-population...');
      const result = await this.autoPopulateAllApps(user);
      console.log('🚀 Force auto-population result:', result);
      return result;
    } catch (error) {
      console.error('❌ Force auto-population failed:', error);
    }
  }
}

// Make testing utilities available globally for easy testing
if (typeof window !== 'undefined') {
  window.resetAutoPopulation = () => autoPopulationService.resetAutoPopulationCheck();
  window.forceAutoPopulation = () => autoPopulationService.forceAutoPopulation();
  window.clearAutoPopulationData = () => autoPopulationService.clearAutoPopulationData();
  // Make getUserArtistId available for apps to use
  window.getUserArtistId = () => autoPopulationService.getUserArtistId();
  // Make getUserArtistName available for apps to use
  window.getUserArtistName = (activities, processedData) => autoPopulationService.getUserArtistName(activities, processedData);
}

const autoPopulationService = new AutoPopulationService();
export default autoPopulationService; 